// WebSocket Service for Real-time Chat Communication
import config from '../config/config';
import { authService } from './authService';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // More attempts for better reliability
    this.reconnectInterval = 3000; // 3 seconds
    this.heartbeatInterval = null;
    this.heartbeatTimeout = 60000; // 60 seconds - more realistic for production
    this.isConnecting = false;
    this.isReconnecting = false; // Flag to track if we're in reconnection mode
    this.listeners = new Map();
    this.messageQueue = [];
    
    // Store connection parameters for reconnection
    this.currentConversationId = null;
    this.currentUserId = null;
  }

  // Connect to WebSocket
  async connect(conversationId, userId) {
    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      ////console.log('WebSocket already connected or connecting');
      return;
    }

    // Store connection parameters for reconnection
    this.currentConversationId = conversationId;
    this.currentUserId = userId;

    this.isConnecting = true;

    if (!conversationId) {
      console.error('No conversation ID provided for WebSocket connection');
      this.isConnecting = false;
      return;
    }

    if (!userId) {
      console.error('No user ID provided for WebSocket connection');
      this.isConnecting = false;
      return;
    }

    try {
      // Get and validate token before connecting
      const token = await this.getValidToken();
      if (!token) {
        console.error('No valid access token available for WebSocket connection');
        this.isConnecting = false;
        return;
      }

      console.log('config.API.Websocket_conversation_Base_URL', config.API.Websocket_conversation_Base_URL);
      // Build WebSocket URL with authentication
      const wsUrl = `${config.API.Websocket_conversation_Base_URL}${conversationId}/?token=${token}&user_id=${userId}`;
      
      //console.log('🔌 Connecting to WebSocket:', wsUrl);
      
      this.socket = new WebSocket(wsUrl);
      this.setupEventListeners();
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  // Get valid token, refresh if necessary
  async getValidToken() {
    try {
      const currentToken = localStorage.getItem('access_token');
      
      if (!currentToken) {
        console.warn('⚠️ No access token found, attempting to refresh...');
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          return refreshResult.accessToken;
        } else {
          console.error('❌ Failed to refresh token:', refreshResult.error);
          // If logout is required, don't attempt reconnection
          if (refreshResult.shouldLogout) {
            console.warn('🔐 Logout required - stopping WebSocket reconnection attempts');
            this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          }
          return null;
        }
      }

      // Verify token is still valid
      const verificationResult = await authService.verifyToken(currentToken);
      if (verificationResult.isValid) {
        return currentToken;
      } else {
        console.warn('⚠️ Access token is invalid, attempting to refresh...');
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          return refreshResult.accessToken;
        } else {
          console.error('❌ Failed to refresh token:', refreshResult.error);
          // If logout is required, don't attempt reconnection
          if (refreshResult.shouldLogout) {
            console.warn('🔐 Logout required - stopping WebSocket reconnection attempts');
            this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          }
          return null;
        }
      }
    } catch (error) {
      console.error('❌ Error getting valid token:', error);
      return null;
    }
  }

  // Handle token expiration during WebSocket connection
  async handleTokenExpiration() {
    //console.log('🔄 Handling token expiration, attempting to refresh...');
    
    try {
      const refreshResult = await authService.refreshAccessToken();
      if (refreshResult.success) {
        //console.log('✅ Token refreshed successfully, reconnecting WebSocket...');
        // Disconnect current connection and reconnect with new token
        this.disconnect();
        // Small delay to ensure clean disconnection
        setTimeout(() => {
          if (this.currentConversationId && this.currentUserId) {
            this.connect(this.currentConversationId, this.currentUserId);
          }
        }, 1000);
        return true;
      } else {
        console.error('❌ Token refresh failed:', refreshResult.error);
        // If logout is required, don't attempt reconnection - logout will handle cleanup
        if (refreshResult.shouldLogout) {
          console.warn('🔐 Logout required - stopping WebSocket reconnection attempts');
          this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          // Disconnect cleanly since logout is in progress
          this.disconnect();
          return false;
        }
        this.emit('token_refresh_failed', { error: refreshResult.error, retryable: refreshResult.retryable });
        return false;
      }
    } catch (error) {
      console.error('❌ Error during token refresh:', error);
      this.emit('token_refresh_failed', { error: error.message });
      return false;
    }
  }

  // Setup WebSocket event listeners
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.onopen = (event) => {
      //console.log('✅ WebSocket connected successfully');
      this.isConnecting = false;
      this.isReconnecting = false; // Clear reconnecting flag on successful connection
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.processMessageQueue();
      this.emit('connected', event);
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        //console.log('📨 WebSocket message received:', data);
        this.handleMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.socket.onclose = async (event) => {
      //console.log('❌ WebSocket connection closed:', event.code, event.reason);
      this.isConnecting = false;
      this.stopHeartbeat();
      
      // Check if closure was due to authentication failure (401)
      if (event.code === 1008 || event.code === 1002 || event.reason?.includes('401') || event.reason?.includes('unauthorized')) {
        //console.log('🔑 WebSocket closed due to authentication failure, attempting token refresh...');
        const refreshSuccess = await this.handleTokenExpiration();
        if (refreshSuccess) {
          return; // Token refresh will trigger reconnection
        }
      }
      
      // Only attempt reconnection if it wasn't a manual close
      if (event.code !== 1000) {
        // Set reconnecting flag and emit 'connecting' status
        this.isReconnecting = true;
        this.emit('connecting', { reason: 'reconnecting' });
        this.handleReconnect();
      } else {
        // Only emit 'disconnected' if it was a manual close
        this.isReconnecting = false;
        this.emit('disconnected', event);
      }
    };

    this.socket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.isConnecting = false;
      
      // Only emit error event, don't emit disconnected during reconnection
      if (!this.isReconnecting) {
        this.emit('error', error);
      } else {
        //console.log('🔄 Error during reconnection - not emitting error event to avoid status change');
      }
    };
  }

  // Handle incoming messages
  handleMessage(data) {
    //console.log('📨 Raw WebSocket message received:', data);
    
    // Handle pong responses to ping messages
    if (data.type === 'pong' || data.event === 'pong') {
      //console.log('🏓 Received pong response, connection is alive');
      if (this.lastPingTimeout) {
        clearTimeout(this.lastPingTimeout);
        this.lastPingTimeout = null;
      }
      return;
    }
    
    // Handle different message formats
    let type, payload;
    
    if (data.event && data.message) {
      // Format: { event: "message.new", message: {...} }
      type = data.event;
      payload = data.message;
      //console.log('🔍 Detected event format: event + message');
    } else if (data.event === 'invite.new' && data.participant) {
      type = 'invite.new';
      payload = data;
    } else if (data.event === 'invite.updated' && data.participant) {
      type = 'invite.updated';
      payload = data;
    } else if (data.event === 'invite.removed' && data.participant) {
      type = 'invite.removed';
      payload = data;
    } else if (data.type && data.payload) {
      // Format: { type: "message", payload: {...} }
      type = data.type;
      payload = data.payload;
      //console.log('🔍 Detected event format: type + payload');
    } else {
      // Fallback: treat entire data as payload
      type = data.type || 'unknown';
      payload = data;
      //console.log('🔍 Using fallback format');
    }
    
    //console.log('📨 Processed message - Type:', type, 'Payload:', payload);
    
    switch (type) {
      case 'message.new':
        // Handle the new message.new event format
        //console.log('📨 Processing message.new event:', payload);
        this.emit('message', payload);
        break;
      // case 'message.connection_established':
        
      //   break;
      case 'message':
        this.emit('message', payload);
        break;
      case 'message_sent':
        this.emit('message_sent', payload);
        break;
      case 'message_updated':
        this.emit('message_updated', payload);
        break;
      case 'message_deleted':
        this.emit('message_deleted', payload);
        break;
      case 'message.status.updated':
        this.emit('message_status_updated', payload);
        break;
      case 'typing':
        this.emit('typing', payload);
        break;
      case 'user_joined':
        this.emit('user_joined', payload);
        break;
      case 'user_left':
        this.emit('user_left', payload);
        break;
      case 'chat_status_changed':
        this.emit('chat_status_changed', payload);
        break;
      case 'invite.new':
        this.emit('invite.new', payload);
        break;
      case 'invite.updated':
        this.emit('invite.updated', payload);
        break;
      case 'invite.removed':
        this.emit('invite.removed', payload);
        break;
      case 'pong':
        // Heartbeat response
        //console.log('💓 Heartbeat response received');
        break;
      default:
        //console.log('❓ Unknown message type:', type, 'Data:', data);
        this.emit('unknown', data);
    }
  }

  // Send message through WebSocket
  sendMessage(messageData) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'message',
        payload: messageData
      };
      
      //console.log('📤 Sending WebSocket message:', message);
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, queuing message');
      this.messageQueue.push(messageData);
    }
  }

  // Send typing indicator
  sendTyping(isTyping) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'typing',
        payload: { is_typing: isTyping }
      };
      
      this.socket.send(JSON.stringify(message));
    }
  }

  // Start heartbeat to keep connection alive and detect stale connections
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Send ping and set up response timeout
        const pingId = Date.now();
        this.socket.send(JSON.stringify({ type: 'ping', ts: pingId }));
        
        // Set up response timeout - if no pong received within 30 seconds, consider connection stale
        const responseTimeout = setTimeout(() => {
          console.warn('⚠️ WebSocket ping timeout - connection may be stale, attempting reconnection...');
          this.handleStaleConnection();
        }, 30000);
        
        // Store timeout for cleanup when pong is received
        this.lastPingTimeout = responseTimeout;
      }
    }, this.heartbeatTimeout);
  }

  // Stop heartbeat
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.lastPingTimeout) {
      clearTimeout(this.lastPingTimeout);
      this.lastPingTimeout = null;
    }
  }

  // Handle stale connection detection
  handleStaleConnection() {
    console.warn('🔄 Detected stale WebSocket connection, forcing reconnection...');
    this.isReconnecting = true;
    this.emit('connecting', { reason: 'stale_connection' });
    
    // Force close the current connection
    if (this.socket) {
      this.socket.close(1000, 'Stale connection detected');
    }
    
    // Attempt reconnection
    this.handleReconnect();
  }

  // Process queued messages
  processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  // Handle reconnection
  async handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached - stopping reconnection attempts');
      this.isReconnecting = false; // Clear reconnecting flag
      this.emit('disconnected', { reason: 'max_attempts_reached' });
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    // Check if we have stored connection parameters
    if (!this.currentConversationId || !this.currentUserId) {
      console.error('❌ Cannot reconnect: missing conversation ID or user ID');
      this.isReconnecting = false; // Clear reconnecting flag
      this.emit('disconnected', { reason: 'missing_parameters' });
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    this.reconnectAttempts++;
    //console.log(`🔄 Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} - retrying in ${this.reconnectInterval}ms`);
    //console.log(`🔄 Reconnecting with conversationId: ${this.currentConversationId}, userId: ${this.currentUserId}`);
    
    // Only emit connecting status on first attempt, keep it in connecting state
    if (this.reconnectAttempts === 1) {
      this.emit('connecting', { 
        reason: 'reconnecting', 
        attempt: this.reconnectAttempts, 
        maxAttempts: this.maxReconnectAttempts 
      });
    }
    
    // Exponential backoff: 3s, 6s, 12s, 24s, 30s (max)
    const backoffDelay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    setTimeout(async () => {
      if (this.socket && this.socket.readyState === WebSocket.CLOSED) {
        // Try to get a fresh token before reconnecting
        try {
          const token = await this.getValidToken();
          if (token) {
            await this.connect(this.currentConversationId, this.currentUserId);
          } else {
            // Check if we should stop reconnection attempts (logout in progress)
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              // No refresh token means logout is in progress - stop reconnection attempts
              console.warn('🔐 No refresh token available - logout in progress, stopping reconnection attempts');
              this.isReconnecting = false;
              this.emit('disconnected', { reason: 'logout_in_progress' });
              return;
            }
            console.error('❌ Cannot reconnect: no valid token available');
            this.emit('token_refresh_failed', { error: 'No valid token available for reconnection' });
          }
        } catch (error) {
          console.error('❌ Error during reconnection:', error);
          this.handleReconnect(); // Try again
        }
      }
    }, backoffDelay);
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Emit event to listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket event listener:', error);
        }
      });
    }
  }

  // Disconnect WebSocket
  disconnect() {
    //console.log('🔌 Disconnecting WebSocket');
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }
    
    this.isConnecting = false;
    this.isReconnecting = false; // Clear reconnecting flag
    this.reconnectAttempts = 0;
    this.messageQueue = [];
    
    // Clear stored connection parameters
    this.currentConversationId = null;
    this.currentUserId = null;
  }

  // Get connection status
  getConnectionStatus() {
    if (!this.socket) return 'disconnected';
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  // Check if connected
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  // Update connection parameters (useful when switching chats)
  updateConnectionParams(conversationId, userId) {
    //console.log('🔄 Updating WebSocket connection parameters:', { conversationId, userId });
    
    // If we're currently connected to a different conversation, disconnect first
    if (this.isConnected() && (this.currentConversationId !== conversationId || this.currentUserId !== userId)) {
      //console.log('🔄 Switching conversations, disconnecting current connection');
      this.disconnect();
    }
    
    // Store new parameters
    this.currentConversationId = conversationId;
    this.currentUserId = userId;
  }

  // Manual reconnect method
  async manualReconnect() {
    //console.log('🔄 Manual reconnect triggered');
    
    // Reset reconnection attempts and set reconnecting flag
    this.reconnectAttempts = 0;
    this.isReconnecting = true;
    
    // Emit connecting status immediately
    this.emit('connecting', { reason: 'manual_reconnect' });
    
    // Disconnect current connection if exists
    if (this.socket) {
      this.socket.close();
    }
    
    // Start reconnection process
    await this.handleReconnect();
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
